<template>
  <div>
    <v-container fluid class="pa-0">
      <v-row dense row>
        <v-col class="col-9">
          <v-row dense row class="mt-1">
            <v-col class="col-12">
              <div
                v-if="folderName"
                class="hyp-folder-name application-heading"
              >
                {{ folderName }}

                <span class="ml-2">({{ itemsCount }})</span>

                <v-icon
                  size="18"
                  class="ml-5 hyp-report-icon"
                  v-if="folderSummaryFeatureUnlocked && isFolderReportAvailable"
                  :title="`${folderName} Folder Report`"
                  @click="showFolderReport = !showFolderReport"
                >
                  fas fa-signal
                </v-icon>

                <v-icon
                  size="18"
                  class="ml-5 hyp-note-icon"
                  v-if="!isLinked || folderNote"
                  :title="folderNotesTitle"
                  @click="showFolderNotes = !showFolderNotes"
                  >{{ folderNotesIcon }}</v-icon
                >

                <v-icon
                  v-if="canDeleteFolder"
                  size="18"
                  class="ml-5 hyp-delete-icon"
                  :title="`Delete folder ${folderName} and its contents`"
                  @click="deleteConfirmationShow = true"
                  >far fa-trash-alt</v-icon
                >

                <v-icon
                  v-if="isShareable"
                  size="18"
                  class="ml-5 hyp-share-icon"
                  :title="`Share folder ${folderName} and its contents`"
                  @click="sharePopupShow = true"
                  >fas fa-share</v-icon
                >

                <v-icon
                  v-if="isLinkable"
                  size="18"
                  class="ml-5 hyp-link-icon"
                  :title="`Link folder ${folderName} and its contents`"
                  @click="linkPopupShow = true"
                  >fas fa-link</v-icon
                >

                <v-icon
                  v-if="isLinked"
                  size="18"
                  class="ml-5 hyp-link-icon"
                  :title="`Unlink folder ${folderName}`"
                  @click="unlinkConfirmationShow = true"
                  >fas fa-unlink</v-icon
                >

                <v-menu :close-on-content-click="true">
                  <template v-slot:activator="{ on }">
                    <v-icon
                      v-on="on"
                      size="18"
                      class="ml-5 hyp-export-icon"
                      title="Export documents"
                      >fas fa-cloud-download-alt</v-icon
                    >
                  </template>
                  <v-card>
                    <v-list>
                      <v-list-item
                        v-if="canExportFolderAsPpt"
                        @click="handleExportPowerPoint"
                      >
                        <v-list-item-title
                          >Export to PowerPoint</v-list-item-title
                        >
                      </v-list-item>
                      <v-list-item @click="handleExportCSV">
                        <v-list-item-title>Export to CSV</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="handleExportDocx">
                        <v-list-item-title>Export to Word</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-menu>
              </div>
            </v-col>
          </v-row>

          <v-row dense v-if="showFolderNotes" row>
            <v-col class="col-12">
              <v-textarea
                v-model="folderNote"
                :disabled="isLinked"
                placeholder="Enter a folder note"
                :rows="3"
                :loading="folderNoteUpdating"
                class="hyp-folder-note-textarea my-0 py-0"
                @change="handleUpdateFolderNote"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-row
            dense
            row
            v-if="folderSummaryFeatureUnlocked && showFolderReport"
          >
            <v-col class="col-12 pl-4 pr-4 pt-4">
              <CollaborationFolderSummary :selected-folder="folder">
              </CollaborationFolderSummary>
            </v-col>
          </v-row>

          <v-row dense row>
            <v-col class="col-12">
              <div v-if="!loading && (!items || !items.length)" class="mt-2">
                No items found
              </div>
              <div>
                <Loader v-if="loading && !items" color="black" text=""></Loader>
              </div>

              <DetailsListCluster
                v-if="items && items.length"
                :items="items"
                :page="itemsPage"
                :page-size="itemsPageSize"
                :more-results="moreResultsAvailable"
                :height-percent="86"
                theme="light"
                :matomo-category="matomoCategory"
                :action-icons-enabled="true"
                :showing-documents="true"
                :show-folder-notes="showFolderNotes"
                :show-thumbs-down-geo="false"
                :show-primary-topics="true"
                :acceptable-primary-topics="eventTypesShownAndSelected"
                :get-threshold-type-function="getThresholdTypeFunction"
                @get-data="getContent"
                @loaded="loading = false"
                @read-click="handleReadClick"
                @unread-click="handleUnreadClick"
                @delete-click="handleDeleteClick"
                @update-doc-note="handleUpdateDocumentNote"
                @label-color-click="handleLabelColorClick"
              ></DetailsListCluster>
            </v-col>
          </v-row>
        </v-col>
        <v-col class="col-3">
          <CollaborationContentFilters
            :refresh="refresh"
            :loading="loading"
            :search-marker="searchMarker"
            @change-search="handleSearchChange"
            @change-topics-filter="handleTopicsFilterChange"
            @change-sort="handleSortChange"
            @change-source="handleSourceChange"
            @change-label-color="handleLabelColorChange"
            @change-date="handleDateChange"
            @change-risk-categories="handleRiskCategoriesFilterChange"
          ></CollaborationContentFilters>
        </v-col>
      </v-row>
    </v-container>

    <ConfirmationPopup
      :show="deleteConfirmationShow"
      :title="`Delete Folder: ${folderName}`"
      :text="`Are you sure you want to delete the folder <strong>${folderName}</strong> and all its data?`"
      :buttonCancel="deleteConfirmationBtnCancel"
      :buttonAction="deleteConfirmationBtnAction"
    ></ConfirmationPopup>

    <ConfirmationPopup
      :show="unlinkConfirmationShow"
      :title="`Unlink Folder: ${folderName}`"
      :text="`Are you sure you want to unlink the folder <strong>${folderName}</strong>?`"
      :buttonCancel="unlinkConfirmationBtnCancel"
      :buttonAction="unlinkConfirmationBtnAction"
    ></ConfirmationPopup>

    <v-dialog v-model="sharePopupShow" width="600" persistent>
      <v-card>
        <v-card-title class="headline"
          >Share Folder: {{ folderName }}</v-card-title
        >
        <v-card-text
          >Please note that removing a colleague will unshare the
          folder.</v-card-text
        >
        <UserShareListEditor
          v-model="folder.sharedWith"
          :init="sharedWithInit"
          width-class-name="col-8"
          class="hyp-shared-listing"
        ></UserShareListEditor>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn text @click="handleShareCancel"> Cancel </v-btn>
          <v-btn
            :disabled="shareSaveBtnDisabled"
            color="success"
            @click="handleShareSave"
          >
            Share
            <Loader
              v-if="shareSaveBtnLoader && shareSaveBtnDisabled"
              size="small"
              color="white"
              text=""
              class="ml-2"
            ></Loader>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="linkPopupShow" width="600" persistent>
      <v-card>
        <v-card-title class="headline"
          >Link Folder: {{ folderName }}</v-card-title
        >
        <CollaborationFolderList
          v-model="folder.linkedWith"
          :folder="folder"
          :init="linkedWithInit"
          width-class-name="col-8"
          class="hyp-link-listing"
        ></CollaborationFolderList>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn text @click="handleLinkCancel"> Cancel </v-btn>
          <v-btn
            :disabled="linkSaveBtnDisabled"
            color="success"
            @click="handleLinkSave"
          >
            Link
            <Loader
              v-if="linkSaveBtnLoader && linkSaveBtnDisabled"
              size="small"
              color="white"
              text=""
              class="ml-2"
            ></Loader>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import lodashGet from "lodash/get";
import lodashFind from "lodash/find";
import lodashIsEqual from "lodash/isEqual";
import lodashDebounce from "lodash/debounce";
import lodashOrderBy from "lodash/orderBy";
import lodashCloneDeep from "lodash/cloneDeep";

import { mapGetters } from "vuex";

import { MODULE_PREFIX_HOME, MODULE_PREFIX_COLLABORATION } from "@/store";
import {
  GET_NOTIFIER,
  GET_COLLABORATION_FOLDERS_CHANGED,
  GET_COLLABORATION_FOLDERS,
} from "@/store/actions.type";
import { getPrimaryTopics } from "@/services/events/topics.service";
import { setInfiniteScrollState } from "@/common/basic.utilities";
import { getSources } from "@/services/alerts/alerts.service";
import {
  getArticlesFromFolder,
  deleteFolder,
  saveFolder,
  saveArticleToFolder,
  deleteArticleFromFolder,
  exportFolderDocuments,
  shareFolder,
  linkFolder,
  unlinkFolder,
} from "@/services/collaboration/folders.service";
import { getTodayStartEndDate } from "@/common/date.utilities";
import { NOTIFIER_TYPES } from "@/common/constants/misc";
import { ICONS } from "@/common/constants/icons";
import { hasTwitterAccess } from "@/services/user/user.service";
import {
  EVENT_CATEGORIES,
  SEARCH_CATEGORIES,
} from "@/common/constants/analytics";

const EMITTED_ACTIONS = {
  CLICK_FOLDER_DELETE: "click-folder-delete",
  CLICK_DOCUMENT_DELETE: "click-document-delete",
  CLICK_FOLDER_LINK: "click-folder-link",
};

export default {
  name: "CollaborationContent",
  components: {
    DetailsListCluster: () =>
      import("@/components/common/details/DetailsListCluster"),
    ConfirmationPopup: () => import("@/components/common/ConfirmationPopup"),
    Loader: () => import("@/components/common/Loader"),
    CollaborationContentFilters: () =>
      import("@/components/collaboration/CollaborationContentFilters"),
    UserShareListEditor: () =>
      import("@/components/alerts/wizard/userShareList/UserShareListEditor"),
    CollaborationFolderList: () =>
      import("@/components/collaboration/CollaborationFolderList"),
    CollaborationFolderSummary: () =>
      import("@/components/collaboration/CollaborationFolderSummary"),
  },
  props: {
    folder: {
      type: Object,
    },
    refresh: {
      type: String,
    },
  },
  data() {
    const sources = getSources();

    let note = null;
    if (lodashGet(this.folder, "comments")) {
      note = this.folder.comments;
    }

    if (!this.folder.linkedWith) {
      this.folder.linkedWith = [];
    }
    const PRIMARY_TOPICS = getPrimaryTopics();
    const SORTED_TOPICS = lodashOrderBy(PRIMARY_TOPICS, ["order"], ["asc"]);
    let types = [];
    SORTED_TOPICS.forEach((topic) => {
      if (this.featureUnlocked(`event-${topic.type}`)) {
        types.push(topic.type);
      }
    });

    return {
      loading: false,
      items: null,
      itemsPage: 1,
      itemsPageSize: 25,
      search: null,
      searchMarker: false,
      possibleSources: sources,
      dateStart: null,
      dateEnd: null,
      deleteConfirmationShow: false,
      deleteBtnDisabled: false,
      deleteBtnText: "Yes, delete it!",
      itemsCount: 0,
      folderNote: note,
      showFolderNotes: false,
      eventTypesSelected: [],
      riskCategoriesSelected: "",
      labelColorSelected: null,
      sortSelected: null,
      folderNoteUpdating: false,
      originalComment: note,
      sharePopupShow: false,
      shareSaveBtnDisabled: false,
      shareSaveBtnLoader: false,
      sharedWithInit: new Date().toISOString(),
      linkPopupShow: false,
      linkSaveBtnDisabled: false,
      linkSaveBtnLoader: false,
      linkedWithInit: null,
      unlinkConfirmationShow: false,
      unlinkBtnDisabled: false,
      unlinkBtnText: "Yes, unlink it!",
      folderSummaryFeatureUnlocked: false,
      showFolderReport: false,
      moreResultsAvailable: false,
      matomoCategory: EVENT_CATEGORIES.collaboration,
    };
  },
  computed: {
    ...mapGetters({
      notifier: MODULE_PREFIX_HOME + "notifier",
    }),
    folderName() {
      if (this.folder && this.folder.name) {
        return this.folder.name;
      }
      return null;
    },
    isShareable() {
      return this.folder && this.folder.isOwner && !this.isLinked;
    },
    isLinkable() {
      return (
        this.folder &&
        this.folder.isOwner &&
        this.folder.parent &&
        (!this.folder.hasOwnProperty("originalFolderId") ||
          this.folder.originalFolderId === this.folder.id)
      );
    },
    isLinked() {
      return (
        this.folder &&
        this.folder.isOwner &&
        this.folder.parent &&
        this.folder.hasOwnProperty("originalFolderId") &&
        this.folder.originalFolderId !== this.folder.id
      );
    },
    unlinkConfirmationBtnCancel() {
      return {
        text: "Cancel",
        onClickFnc: () => {
          this.setUnlinkConfirmationShow(false);
        },
      };
    },
    unlinkConfirmationBtnAction() {
      return {
        text: this.unlinkBtnText,
        disabled: this.unlinkBtnDisabled,
        loader: true,
        onClickFnc: () => {
          this.handleUnlinkFolder();
        },
      };
    },
    hasFolderNote() {
      return this.folder && this.folder.comments;
    },
    folderNotesTitle() {
      return this.hasFolderNote ? "Folder note added" : "Add folder note";
    },
    folderNotesIcon() {
      return this.hasFolderNote ? "fas fa-comment" : "far fa-comment";
    },
    calendarIcon() {
      const ICON_INACTIVE = ICONS.misc.datePicker.inactive;
      return require(`@/assets/images${ICON_INACTIVE}`);
    },
    deleteConfirmationBtnCancel() {
      return {
        text: "Cancel",
        onClickFnc: () => {
          this.setDeleteConfirmationShow(false);
        },
      };
    },
    deleteConfirmationBtnAction() {
      return {
        text: this.deleteBtnText,
        disabled: this.deleteBtnDisabled,
        loader: true,
        onClickFnc: () => {
          this.handleDeleteFolder();
        },
      };
    },
    canDeleteFolder() {
      return (
        (!this.folder.children ||
          (Array.isArray(this.folder.children) &&
            this.folder.children.length === 1 &&
            !this.folder.children[0].hasOwnProperty("id"))) &&
        !this.isLinked
      );
    },
    canExportFolderAsPpt() {
      return this.featureUnlocked("collaboration-export-ppt");
    },
    news() {
      return lodashFind(this.possibleSources, ["id", "news-all"]);
    },
    social() {
      return hasTwitterAccess()
        ? lodashFind(this.possibleSources, ["id", "twitter"])
        : null;
    },
    eventTypesShown() {
      const PRIMARY_TOPICS = getPrimaryTopics();
      const SORTED_TOPICS = lodashOrderBy(PRIMARY_TOPICS, ["order"], ["asc"]);
      let types = [];
      SORTED_TOPICS.forEach((topic) => {
        if (this.featureUnlocked(`event-${topic.type}`)) {
          types.push(topic.type);
        }
      });
      types.push("all");
      return types;
    },
    eventTypesShownAndSelected() {
      let shown = this.eventTypesShown;
      let selected = this.eventTypesSelected;
      let both = [];
      for (let shownIndex = 0; shownIndex < shown.length; shownIndex++) {
        for (
          let selectedIndex = 0;
          selectedIndex < selected.length;
          selectedIndex++
        ) {
          if (shown[shownIndex] === selected[selectedIndex]) {
            both.push(selected[selectedIndex]);
          }
        }
      }
      return both;
    },
    isFolderReportAvailable() {
      return this.folder.isOwner || this.folder.is_report_visible;
    },
  },
  mounted() {
    this.getContent();
    this.folderSummaryFeatureUnlocked = this.featureUnlocked(
      "collaboration-folder-summary"
    );
  },
  methods: {
    async getContent(page, $state) {
      let params;
      let result;
      try {
        this.loading = true;

        page = !page ? 1 : page;
        const PAGE_OFFSET = (page - 1) * this.itemsPageSize;

        if (this.folder.documents && PAGE_OFFSET < this.folder.documents) {
          params = {
            folder: this.folder,
            pageSize: this.itemsPageSize,
            pageOffset: PAGE_OFFSET,
            source: this.selectedSources ? this.selectedSources : null,
          };

          if (this.dateStart) {
            params.start = getTodayStartEndDate(this.dateStart).start;
          }
          if (this.dateEnd) {
            params.end = getTodayStartEndDate(this.dateEnd).end;
          }
          if (this.search) {
            params.search = this.search;
          }
          if (
            this.eventTypesSelected &&
            Array.isArray(this.eventTypesSelected) &&
            this.eventTypesSelected.length
          ) {
            params.topics = this.eventTypesSelected.join();
          }
          if (this.labelColorSelected && this.labelColorSelected.value) {
            params.color = this.labelColorSelected.value;
          }

          if (this.sortSelected && this.sortSelected.value) {
            params.sortField = this.sortSelected.value;
            params.sortDirection = "desc";
          }

          if (this.riskCategoriesSelected) {
            params.riskCategories = this.riskCategoriesSelected;
          }

          if (!page || page === 1) {
            this.items = null;
          }
          result = await getArticlesFromFolder(params);
          this.moreResultsAvailable = page * this.itemsPageSize < result.total;

          if (result && result.data) {
            if (
              this.items &&
              Array.isArray(this.items) &&
              this.items.length &&
              page !== 1
            ) {
              this.items = this.items.concat(result.data);
            } else {
              this.items = result.data;
              this.itemsCount = result.total || 0;
            }
          } else {
            this.items = null;
            this.itemsCount = 0;
          }
        }
      } catch (error) {
        console.error("Error in getContent: " + JSON.stringify(error));
      } finally {
        setInfiniteScrollState($state, { features: result }, "features");
        this.itemsPage = page;
        this.loading = false;
        result = null;
        params = null;
      }
    },
    handleReadClick(documentId) {
      this.readUnreadArticle(documentId, true);
    },
    handleUnreadClick(documentId) {
      this.readUnreadArticle(documentId, false);
    },
    async handleDeleteClick(documentId) {
      await deleteArticleFromFolder({
        folder: this.folder,
        article: {
          id: documentId,
        },
      });
      this.getContent();
      this.$emit(EMITTED_ACTIONS.CLICK_DOCUMENT_DELETE, true);
    },
    readUnreadArticle(documentId, read) {
      saveArticleToFolder({
        folder: this.folder,
        article: {
          id: documentId,
        },
        doc_metadata: {
          read: read,
        },
      });
    },
    handleSearchChange(search) {
      this.searchMarker = !this.searchMarker;
      this.search = !this.searchMarker ? null : search;
      this.getContent();
    },
    handleTopicsFilterChange(items) {
      this.eventTypesSelected = lodashCloneDeep(items);
      this.getContent();
    },
    handleRiskCategoriesFilterChange(items) {
      this.riskCategoriesSelected = items.riskCategories;
      this.getContent();
    },
    handleSortChange(sort) {
      this.sortSelected = sort && sort.value ? sort : null;
      this.getContent();
    },
    handleSourceChange(source) {
      this.selectedSources = source.value;
      this.getContent();
    },
    handleLabelColorChange(color) {
      this.labelColorSelected = color && color.value ? color : null;
      this.getContent();
    },
    handleDateChange(dates) {
      if (
        dates &&
        dates.hasOwnProperty("start") &&
        dates.hasOwnProperty("end")
      ) {
        this.dateStart = dates.start;
        this.dateEnd = dates.end;
      }
      this.getContent();
    },
    setDeleteConfirmationShow(value) {
      this.deleteConfirmationShow = value;
    },
    setUnlinkConfirmationShow(value) {
      this.unlinkConfirmationShow = value;
    },
    async handleDeleteFolder() {
      this.deleteBtnDisabled = true;
      const RESULT = await deleteFolder({
        folder: this.folder,
      });
      this.notifier.visible = true;
      if (RESULT) {
        this.notifier.type = NOTIFIER_TYPES.success;
        this.notifier.message = "The folder and its data have been deleted!";
      } else {
        this.notifier.type = NOTIFIER_TYPES.error;
        this.notifier.message = "Unable to delete folder.<br>Please try again";
      }
      this.$store.dispatch(MODULE_PREFIX_HOME + GET_NOTIFIER, this.notifier);
      this.$emit(EMITTED_ACTIONS.CLICK_FOLDER_DELETE, true);
      this.deleteBtnDisabled = false;
      this.setDeleteConfirmationShow(false);
    },
    handleUpdateFolderNote() {
      this.folderNoteUpdating = true;
      this.folder.comments = this.folderNote;
      lodashDebounce(async () => {
        try {
          await saveFolder({
            folder: this.folder,
          });
          await this.$store.dispatch(
            MODULE_PREFIX_COLLABORATION + GET_COLLABORATION_FOLDERS_CHANGED,
            true
          );
          await this.$store.dispatch(
            MODULE_PREFIX_COLLABORATION + GET_COLLABORATION_FOLDERS,
            { force: true }
          );
        } catch (err) {
          this.folderNote = this.originalComment;
          this.notifier.visible = true;
          this.notifier.type = NOTIFIER_TYPES.error;
          this.notifier.message = "Failed : " + err.message;
          this.$store.dispatch(
            MODULE_PREFIX_HOME + GET_NOTIFIER,
            this.notifier
          );
        }

        this.folderNoteUpdating = false;
      }, 0)();
    },
    async handleUpdateDocumentNote(obj) {
      try {
        await saveArticleToFolder({
          folder: this.folder,
          article: {
            id: obj.documentId,
          },
          doc_metadata: {
            comments: obj.comments,
          },
        });
        obj.finished();
      } catch (err) {
        obj.revert();
        this.notifier.visible = true;
        this.notifier.type = NOTIFIER_TYPES.error;
        this.notifier.message = "Failed : " + err.message;
        this.$store.dispatch(MODULE_PREFIX_HOME + GET_NOTIFIER, this.notifier);
      }
    },
    handleLabelColorClick(obj) {
      saveArticleToFolder({
        folder: this.folder,
        article: {
          id: obj.documentId,
        },
        doc_metadata: {
          color: obj.color,
        },
      });
    },
    getEventTypesToGrab() {
      let eventTypesToGrab = [];
      if (this.eventTypesSelected) {
        for (let i = 0; i < this.eventTypesSelected.length; i++) {
          if (this.eventTypesTotals[this.eventTypesSelected[i]] !== 0) {
            eventTypesToGrab.push(this.eventTypesSelected[i]);
          }
        }
      }
      return eventTypesToGrab;
    },
    async handleExport(type) {
      const PARAMS = {
        type: type,
        folder: this.folder,
      };
      const RESULT = await exportFolderDocuments(PARAMS);
      let fullTypeName = null;
      switch (type) {
        case "ppt":
          fullTypeName = "PowerPoint";
          break;
        case "csv":
          fullTypeName = "CSV";
          break;
        case "docx":
          fullTypeName = "Word";
          break;
        default:
          fullTypeName = "File Type Not Found";
      }
      if (RESULT) {
        window.open(RESULT, "_blank");
        this.notifier.seconds = 10;
        this.notifier.type = NOTIFIER_TYPES.success;
        this.notifier.message = `Successfully generated ${fullTypeName} export. If a new browser window or browser tab did not appear please check if you have a browser popup blocker, which may be blocking it.`;
      } else {
        this.notifier.type = NOTIFIER_TYPES.error;
        this.notifier.message = `Unable to generate ${fullTypeName} export`;
      }
      this.notifier.visible = true;
      this.$store.dispatch(MODULE_PREFIX_HOME + GET_NOTIFIER, this.notifier);
    },
    handleExportPowerPoint() {
      this.handleExport("ppt");
    },
    handleExportCSV() {
      this.handleExport("csv");
    },
    handleExportDocx() {
      this.handleExport("docx");
    },
    async handleShareSave() {
      this.shareSaveBtnDisabled = true;
      this.shareSaveBtnLoader = true;
      const PARAMS = {
        folder: this.folder,
      };
      const RESULT = await shareFolder(PARAMS);

      if (RESULT) {
        this.notifier.seconds = 7;
        this.notifier.type = NOTIFIER_TYPES.success;
        this.notifier.message = "Successfully shared folder";
      } else {
        this.notifier.type = NOTIFIER_TYPES.error;
        this.notifier.message = "Unable to share folder";
      }
      this.notifier.visible = true;
      this.$store.dispatch(MODULE_PREFIX_HOME + GET_NOTIFIER, this.notifier);

      this.shareSaveBtnDisabled = false;
      this.shareSaveBtnLoader = false;
      this.sharePopupShow = false;
      this.sharedWithInit = new Date().toISOString();
    },
    handleShareCancel() {
      this.shareSaveBtnDisabled = false;
      this.shareSaveBtnLoader = false;
      this.sharePopupShow = false;
      this.sharedWithInit = new Date().toISOString();
    },
    async handleLinkSave() {
      this.linkSaveBtnDisabled = true;
      this.linkSaveBtnLoader = true;
      const PARAMS = {
        folder: this.folder,
      };
      const RESULT = await linkFolder(PARAMS);

      if (RESULT) {
        this.notifier.seconds = 7;
        this.notifier.type = NOTIFIER_TYPES.success;
        this.notifier.message = "Successfully linked folder";
      } else {
        this.notifier.type = NOTIFIER_TYPES.error;
        this.notifier.message = "Unable to link folder";
      }
      this.notifier.visible = true;
      this.$store.dispatch(MODULE_PREFIX_HOME + GET_NOTIFIER, this.notifier);

      this.$emit(EMITTED_ACTIONS.CLICK_FOLDER_LINK, true);

      this.linkSaveBtnDisabled = false;
      this.linkSaveBtnLoader = false;
      this.linkPopupShow = false;
    },
    handleLinkCancel() {
      this.linkSaveBtnDisabled = false;
      this.linkSaveBtnLoader = false;
      this.linkPopupShow = false;
    },
    async handleUnlinkFolder() {
      this.unlinkBtnDisabled = true;
      const RESULT = await unlinkFolder({
        folder: this.folder,
      });
      this.notifier.visible = true;
      if (RESULT) {
        this.notifier.type = NOTIFIER_TYPES.success;
        this.notifier.message = "The folder has been unlinked!";
      } else {
        this.notifier.type = NOTIFIER_TYPES.error;
        this.notifier.message = "Unable to unlink folder.<br>Please try again";
      }
      this.$store.dispatch(MODULE_PREFIX_HOME + GET_NOTIFIER, this.notifier);
      this.$emit(EMITTED_ACTIONS.CLICK_FOLDER_LINK, true);
      this.unlinkBtnDisabled = false;
      this.setUnlinkConfirmationShow(false);
    },
    getThresholdTypeFunction(doc) {
      return lodashGet(doc, "document.source") === "twitter"
        ? "twitter"
        : "cluster";
    },
  },
  watch: {
    folder: {
      handler(newValue, oldValue) {
        if (!lodashIsEqual(newValue, oldValue)) {
          this.items = null;
          this.itemsPage = 1;
          this.itemsCount = 0;
          this.loading = true;
          this.showFolderNotes = false;
          this.folderNote = null;
          if (lodashGet(this.folder, "comments")) {
            this.folderNote = this.folder.comments;
          }
          this.getContent();
          this.sharedWithInit = new Date().toISOString();
          if (newValue.name !== oldValue.name) {
            this.showFolderReport = false;
          }
        }
      },
    },
    refresh: {
      handler(newValue, oldValue) {
        if (newValue != oldValue) {
          this.search = null;
          this.dateStart = null;
          this.dateEnd = null;
          this.selectedSources = !this.social
            ? this.news.id
            : "news-all,twitter";
          this.searchMarker = false;
          this.getContent();
        }
      },
    },
    linkPopupShow: {
      handler() {
        if (this.linkPopupShow) {
          this.linkedWithInit = new Date().toISOString();
        }
      },
    },
  },
  beforeDestroy() {
    this.items = null;
    this.possibleSources = null;
    this.eventTypes = null;
    this.eventTypesSelected = null;
  },
};
</script>

<style scoped>
.hyp-folder-name {
  font-size: 24px !important;
}
.hyp-calendar-icon {
  cursor: pointer;
}
.hyp-delete-icon {
  position: relative;
  top: -3px;
  cursor: pointer;
}
.hyp-note-icon {
  position: relative;
  top: -2px;
  cursor: pointer;
}
.hyp-export-icon {
  position: relative;
  top: -2px;
  cursor: pointer;
}
.hyp-share-icon {
  position: relative;
  top: -2px;
  cursor: pointer;
}
.hyp-shared-listing {
  max-height: 300px;
  overflow-y: scroll;
}
.hyp-link-icon {
  position: relative;
  top: -2px;
  cursor: pointer;
}
.hyp-link-listing {
  max-height: 300px;
  overflow-y: scroll;
}
.hyp-folder-note-textarea {
  font-size: 14px;
  width: 93%;
}
.hyp-report-icon {
  position: relative;
  top: -3px;
  cursor: pointer;
}
</style>
